import React from 'react';
import Icon from '../../../components/AppIcon';

const DataQualityIndicator = ({ data, loading }) => {
  const calculateQualityMetrics = () => {
    if (!data || data?.length === 0) {
      return {
        completeness: 0,
        accuracy: 0,
        consistency: 0,
        timeliness: 0,
        overall: 0,
        issues: []
      };
    }

    const totalRecords = data?.length;
    let completeRecords = 0;
    let accurateRecords = 0;
    let consistentRecords = 0;
    let timelyRecords = 0;
    const issues = [];

    // Check each record for quality metrics
    data?.forEach((record, index) => {
      let recordComplete = true;
      let recordAccurate = true;
      let recordConsistent = true;
      let recordTimely = true;

      // Completeness check
      const requiredFields = ['date', 'contract', 'exchange', 'open', 'high', 'low', 'close', 'volume'];
      const missingFields = requiredFields?.filter(field => 
        record?.[field] === null || record?.[field] === undefined || record?.[field] === ''
      );
      
      if (missingFields?.length > 0) {
        recordComplete = false;
        if (missingFields?.length > 2) {
          issues?.push(`Record ${index + 1}: Missing critical fields (${missingFields?.join(', ')})`);
        }
      }

      // Accuracy check (basic validation)
      if (record?.high < record?.low) {
        recordAccurate = false;
        issues?.push(`Record ${index + 1}: High price is less than low price`);
      }
      
      if (record?.open < 0 || record?.high < 0 || record?.low < 0 || record?.close < 0) {
        recordAccurate = false;
        issues?.push(`Record ${index + 1}: Negative price values detected`);
      }

      if (record?.volume < 0) {
        recordAccurate = false;
        issues?.push(`Record ${index + 1}: Negative volume detected`);
      }

      // Consistency check
      if (index > 0) {
        const prevRecord = data?.[index - 1];
        const priceChange = Math.abs((record?.close - prevRecord?.close) / prevRecord?.close);
        
        if (priceChange > 0.5) { // 50% change threshold
          recordConsistent = false;
          issues?.push(`Record ${index + 1}: Unusual price movement (${(priceChange * 100)?.toFixed(1)}%)`);
        }
      }

      // Timeliness check (assuming data should be recent)
      const recordDate = new Date(record.date);
      const daysDiff = (new Date() - recordDate) / (1000 * 60 * 60 * 24);
      
      if (daysDiff > 365) { // Data older than 1 year
        recordTimely = false;
      }

      if (recordComplete) completeRecords++;
      if (recordAccurate) accurateRecords++;
      if (recordConsistent) consistentRecords++;
      if (recordTimely) timelyRecords++;
    });

    const completeness = (completeRecords / totalRecords) * 100;
    const accuracy = (accurateRecords / totalRecords) * 100;
    const consistency = (consistentRecords / totalRecords) * 100;
    const timeliness = (timelyRecords / totalRecords) * 100;
    const overall = (completeness + accuracy + consistency + timeliness) / 4;

    return {
      completeness,
      accuracy,
      consistency,
      timeliness,
      overall,
      issues: issues?.slice(0, 10) // Limit to first 10 issues
    };
  };

  const getQualityColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getQualityBgColor = (score) => {
    if (score >= 90) return 'bg-success/10';
    if (score >= 70) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const getQualityIcon = (score) => {
    if (score >= 90) return 'CheckCircle';
    if (score >= 70) return 'AlertTriangle';
    return 'XCircle';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-sm p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-muted rounded"></div>
            <div className="h-3 bg-muted rounded w-5/6"></div>
            <div className="h-3 bg-muted rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  const metrics = calculateQualityMetrics();

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="flex items-center gap-2 p-4 border-b border-border">
        <Icon name="Shield" size={20} />
        <h3 className="text-lg font-semibold text-foreground">Data Quality</h3>
        <div className={`ml-auto px-2 py-1 rounded-full text-xs font-medium ${getQualityBgColor(metrics?.overall)} ${getQualityColor(metrics?.overall)}`}>
          {metrics?.overall?.toFixed(1)}% Overall
        </div>
      </div>
      <div className="p-4 space-y-4">
        {/* Quality Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${getQualityBgColor(metrics?.completeness)}`}>
              <Icon name={getQualityIcon(metrics?.completeness)} size={20} className={getQualityColor(metrics?.completeness)} />
            </div>
            <div className="text-sm font-medium text-foreground">Completeness</div>
            <div className={`text-lg font-bold ${getQualityColor(metrics?.completeness)}`}>
              {metrics?.completeness?.toFixed(1)}%
            </div>
          </div>

          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${getQualityBgColor(metrics?.accuracy)}`}>
              <Icon name={getQualityIcon(metrics?.accuracy)} size={20} className={getQualityColor(metrics?.accuracy)} />
            </div>
            <div className="text-sm font-medium text-foreground">Accuracy</div>
            <div className={`text-lg font-bold ${getQualityColor(metrics?.accuracy)}`}>
              {metrics?.accuracy?.toFixed(1)}%
            </div>
          </div>

          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${getQualityBgColor(metrics?.consistency)}`}>
              <Icon name={getQualityIcon(metrics?.consistency)} size={20} className={getQualityColor(metrics?.consistency)} />
            </div>
            <div className="text-sm font-medium text-foreground">Consistency</div>
            <div className={`text-lg font-bold ${getQualityColor(metrics?.consistency)}`}>
              {metrics?.consistency?.toFixed(1)}%
            </div>
          </div>

          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${getQualityBgColor(metrics?.timeliness)}`}>
              <Icon name={getQualityIcon(metrics?.timeliness)} size={20} className={getQualityColor(metrics?.timeliness)} />
            </div>
            <div className="text-sm font-medium text-foreground">Timeliness</div>
            <div className={`text-lg font-bold ${getQualityColor(metrics?.timeliness)}`}>
              {metrics?.timeliness?.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Data Sources */}
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-3">Data Sources</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-sm text-foreground">ICE Exchange Feed</span>
              </div>
              <span className="text-xs text-muted-foreground">Real-time</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-sm text-foreground">NYMEX Data</span>
              </div>
              <span className="text-xs text-muted-foreground">15min delay</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-warning rounded-full"></div>
                <span className="text-sm text-foreground">EEX Historical</span>
              </div>
              <span className="text-xs text-muted-foreground">End of day</span>
            </div>
          </div>
        </div>

        {/* Issues */}
        {metrics?.issues?.length > 0 && (
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              Data Quality Issues
            </h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {metrics?.issues?.map((issue, index) => (
                <div key={index} className="text-xs text-muted-foreground p-2 bg-warning/5 rounded">
                  {issue}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Last updated: {new Date()?.toLocaleString()}
            </span>
            <span className="text-muted-foreground">
              {data?.length || 0} records analyzed
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataQualityIndicator;